import { useCallback, useEffect, useState } from 'react';
import type { AlertFrequency, EmailAlertsStatus } from '../types/email-alerts';

export interface EmailAlertsSubscriptionRecord {
  status: EmailAlertsStatus;
  frequency: AlertFrequency;
  locale: 'en' | 'fi' | 'sv';
  email: string | null;
  lastSentAt: number | null;
}

export interface UseEmailAlertsStatusResult {
  /** null while loading, or once loaded if the user has no subscription yet. */
  subscription: EmailAlertsSubscriptionRecord | null;
  loading: boolean;
  /** Re-fetch after a mutation elsewhere (subscribe/unsubscribe) without duplicating fetch/parse logic. */
  refresh: () => Promise<void>;
}

/**
 * Fetches the signed-in user's master email-alerts subscription (GET /api/alerts/subscription).
 * Shared by JobAlertsPanel (saved-searches page) and SrpAlertCta (SRP) — both need to know whether
 * alert emails are already on before offering to turn them on.
 */
export function useEmailAlertsStatus(enabled: boolean): UseEmailAlertsStatusResult {
  const [subscription, setSubscription] = useState<EmailAlertsSubscriptionRecord | null>(null);
  const [loading, setLoading] = useState(enabled);

  const fetchStatus = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/alerts/subscription');
      if (!response.ok) throw new Error(`status ${response.status}`);
      const body = await response.json();
      setSubscription(body.subscription ?? null);
    } catch {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  return { subscription, loading, refresh: fetchStatus };
}
